import { css, CSSObject } from '@emotion/react'
import { useMemo } from 'react'
import { Color, Spacing, Typography } from '@opallabs/gem'
import { track } from '@opallabs/quanta'
import { colors } from '@opallabs/spectrum'
import { AssignmentIcon } from '../AssignmentHelpers/AssignmentIcon'
import { AssignmentStar } from '../AssignmentHelpers/AssignmentStar'
import { AssignmentText } from '../AssignmentHelpers/AssignmentText'
import { backgroundByStatus } from '../AssignmentHelpers/helpers'
import { TaskCheckbox } from './TaskCheckbox'
import { TaskDueDate } from './TaskDueDate'
import { TaskProps } from './types'

const taskItem = (starred: boolean): CSSObject => ({
  backgroundColor: Color.light.Surface.N000,
  color: Color.light.Text.Default,
  alignItems: 'center',
  display: 'flex',
  cursor: 'pointer',
  padding: Spacing.S * 1.25,

  '.kuiper-checkbox-frame .kuiper-icon-wrapper': { boxSizing: 'content-box' },
  '.task-star .kuiper-icon-wrapper': {
    inlineSize: starred ? Spacing.S * 1.75 : 0,
    marginInlineStart: starred ? Spacing.S : 0,
    marginInlineEnd: starred ? Spacing.XS * 1.25 : 0,
  },
  '&:hover': {
    backgroundColor: Color.light.Translucent.A08,

    '.task-star': {
      opacity: 1,
    },

    '.task-star .kuiper-icon-wrapper': {
      inlineSize: Spacing.S * 1.75,
      marginInlineEnd: Spacing.XS * 1.25,
      marginInlineStart: Spacing.S,
    },
  },
  '.kuiper-popover-base': {
    display: 'flex',
  },
})

const taskContext = css({
  display: 'flex',
  marginBlockStart: 1,

  '.kuiper-icon-wrapper': {
    marginInlineEnd: Spacing.S * 1.25,
  },
})

export const taskContextText = css({
  ...Typography.T300,
  maxInlineSize: Spacing.XL * 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const trackCompleteTask = (): void =>
  track('Task: Complete', {
    completed: 'Assigned',
    workflowPopover: false,
  })

const trackStarredTask = (starred: boolean): void =>
  track('Task: Star Toggled', {
    starred,
    View: 'Personal Panel',
  })

export const TaskItem: React.FC<TaskProps> = ({
  checked,
  completedAt,
  contextLiveDate,
  contextName,
  disableCheckbox,
  dueDate,
  hardDueDate,
  iconColor,
  iconName,
  id,
  phaseIsComplete,
  phaseIsCurrent,
  onCheckboxClick,
  onItemClick,
  onStarClick,
  overdue = false,
  starrable,
  starred,
  taskStatus,
  taskText,
}) => {
  const toggleTaskCompletion = (value: boolean): void => {
    if (!checked) {
      trackCompleteTask()
    }
    onCheckboxClick(value)
  }

  const backgroundStyles = useMemo(
    () => backgroundByStatus(taskStatus),
    [taskStatus]
  )

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <li
      css={css([taskItem(starred), backgroundStyles])}
      onClick={onItemClick}
      key={`task-item-${id}`}
    >
      <TaskCheckbox
        checked={checked}
        taskText={taskText}
        iconName={iconName}
        dueDate={dueDate}
        completedAt={completedAt}
        disableCheckbox={disableCheckbox}
        phaseIsComplete={phaseIsComplete}
        phaseIsCurrent={phaseIsCurrent}
        onCheckboxClick={toggleTaskCompletion}
      />

      <div css={taskContext}>
        <AssignmentIcon
          css={{
            marginInlineStart: Spacing.S * 1.25,
          }}
          iconName={iconName}
          color={iconColor}
        />
        <div css={taskContextText}>{contextName}</div>
      </div>
      <AssignmentIcon
        css={{ marginLeft: '10px' }}
        iconName="chevron-e"
        color={colors.n6.base}
        height={10}
        width={10}
      />
      <AssignmentText
        text={taskText}
        styleOverride={checked ? { textDecoration: 'line-through' } : {}}
      />

      {dueDate !== undefined && (
        <TaskDueDate
          contextLiveDate={contextLiveDate}
          hardDueDate={hardDueDate}
          dueDate={dueDate}
          overdue={overdue}
        />
      )}

      {starrable && (
        <AssignmentStar
          starred={starred}
          onClick={(e: React.MouseEvent<HTMLElement>): void => {
            trackStarredTask(!starred)
            onStarClick(e)
          }}
        />
      )}
    </li>
  )
}
